import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { trackCTAClick } from '../utils/analytics';
import BrandLogo from './BrandLogo';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Challenges', href: '#challenges' },
    { label: 'Solutions', href: '#solutions' },
    { label: 'Process', href: '#process' },
    { label: 'AI', href: '#ai' },
    { label: 'Services', href: '#services' },
    { label: 'Workshops', href: '/workshops', isExternal: true },
    { label: 'Events', href: '/events', isExternal: true },
    { label: 'Case Studies', href: '/case-studies', isExternal: true },
    { label: 'DISC Quiz', href: '/find-your-bird', isExternal: true },
    { label: 'Free Assessment', href: '/assessment', isExternal: true },
    { label: 'Contact', href: '#consultation' }
  ];

  const scrollToSection = (href, isExternal = false) => {
    if (isExternal) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    
    if (href === '#consultation') {
      trackCTAClick('Schedule a Strategy Session', 'header');
    }
    
    // If not on landing page, navigate home first then scroll
    if (!isLandingPage) {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-950/95 backdrop-blur-md shadow-lg shadow-black/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
            <BrandLogo
              className="h-14 sm:h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              fallbackClassName="text-base sm:text-lg font-bold tracking-wide text-white whitespace-nowrap"
              testId="header-logo-img"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href, item.isExternal)}
                className={`text-sm font-medium transition-colors duration-200 ${
                  item.label === 'Free Assessment' 
                    ? 'text-cyan-400 hover:text-cyan-300' 
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid={item.label === 'Free Assessment' ? 'nav-assessment-link' : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={() => scrollToSection('#consultation')}
              data-testid="header-cta-button"
              className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-5 transition-all duration-200"
            >
              Schedule a Strategy Session
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-16 sm:top-20 left-0 right-0 bg-slate-950/98 backdrop-blur-lg border-t border-slate-800">
            <nav className="flex flex-col py-4 px-4 gap-1">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href, item.isExternal)}
                  className={`text-left py-3 text-lg border-b border-slate-800/50 transition-colors duration-200 ${
                    item.label === 'Free Assessment' 
                      ? 'text-cyan-400 hover:text-cyan-300' 
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid={item.label === 'Free Assessment' ? 'mobile-nav-assessment-link' : undefined}
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('#consultation')}
                data-testid="mobile-header-cta-button"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold mt-4"
              >
                Schedule a Strategy Session
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
